import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import TotalRecordsCard from '../components/charts/TotalRecordsCard';
import GenderDistributionChart from '../components/charts/GenderDistributionChart';
import BirthsByYearChart from '../components/charts/BirthsByYearChart';
import BirthsByMonthChart from '../components/charts/BirthsByMonthChart';
import BirthsByClinicChart from '../components/charts/BirthsByClinicChart';

// Configure axios for API calls
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  withCredentials: true
});

const AdminDashboardPage = () => {
  const { user } = useAuth();
  
  const [dashboardData, setDashboardData] = useState({
    totalRecords: 0,
    genderStats: [],
    yearlyTrend: [],
    clinicStats: [],
    monthlyTrend: []
  });
  
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedClinic, setSelectedClinic] = useState('');
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (availableYears.length > 0) {
      loadDashboardData();
    }
  }, [availableYears, selectedYear, selectedClinic]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Fetch available years from DOB
      const yearsResponse = await api.get('/api/dashboard/years');
      setAvailableYears(yearsResponse.data);
      
      // Fetch clinics for admin filter
      const clinicsResponse = await api.get('/api/clinics');
      setClinics(clinicsResponse.data);
      
      setLoading(false);
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Failed to load initial data');
      setLoading(false);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build params object
      const params = {};
      if (selectedYear) {
        params.year = selectedYear;
      }
      
      // Add clinic filter if selected
      if (selectedClinic) {
        params.clinic_id = selectedClinic;
      }

      // Prepare yearly trend params (respects clinic filter but not year filter)
      const yearlyTrendParams = {};
      if (selectedClinic) {
        yearlyTrendParams.clinic_id = selectedClinic;
      }

      // Prepare monthly params (includes year if selected)
      const monthParams = { ...params };

      // Fetch all dashboard metrics
      const clinicStatsParams = {};
      if (selectedYear) {
        clinicStatsParams.year = selectedYear;
      }
      if (selectedClinic) {
        clinicStatsParams.clinic_id = selectedClinic;
      }

      const [totalRes, genderRes, yearlyRes, monthlyRes, clinicRes] = await Promise.all([
        api.get('/api/dashboard/total-records', { params }),
        api.get('/api/dashboard/gender-stats', { params }),
        api.get('/api/dashboard/yearly-trend', { params: yearlyTrendParams }),
        api.get('/api/dashboard/monthly-trend', { params: monthParams }),
        api.get('/api/dashboard/clinic-stats', { params: clinicStatsParams })
      ]);

      let processedYearlyData = yearlyRes.data || [];
      if (selectedYear) {
        processedYearlyData = processedYearlyData.filter(
          (item) => item.year === parseInt(selectedYear, 10)
        );
      }

      setDashboardData({
        totalRecords: totalRes.data.total,
        genderStats: genderRes.data,
        yearlyTrend: processedYearlyData,
        monthlyTrend: monthlyRes.data,
        clinicStats: clinicRes.data
      });

      setLoading(false);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSelectedYear('');
    setSelectedClinic('');
  };

  if (loading && availableYears.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error && !loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <p className="text-red-800 font-medium">{error}</p>
          <button 
            onClick={loadDashboardData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const selectedClinicName = selectedClinic ? 
    clinics.find(c => c.clinic_id === selectedClinic)?.clinic_name : 
    null;

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-4">
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Welcome, <span className="font-semibold">{user?.full_name || user?.username}</span>
          </p>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-lg shadow-md p-5 mb-5">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Year Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Year
              </label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Years</option>
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            {/* Clinic Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Clinic
              </label>
              <select
                value={selectedClinic}
                onChange={(e) => setSelectedClinic(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Clinics</option>
                {clinics.map(clinic => (
                  <option key={clinic.clinic_id} value={clinic.clinic_id}>
                    {clinic.clinic_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Clear Filters Button */}
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Total Records Card */}
        <TotalRecordsCard 
          totalRecords={dashboardData.totalRecords}
          selectedYear={selectedYear}
          clinicName={selectedClinicName}
        />

        {/* Charts Grid - Top Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
          <GenderDistributionChart data={dashboardData.genderStats} />
          <BirthsByYearChart 
            data={dashboardData.yearlyTrend}
            selectedYear={selectedYear}
            clinicName={selectedClinicName}
          />
        </div>

        {/* Charts Grid - Bottom Row */}
        <div className={`grid grid-cols-1 ${dashboardData.monthlyTrend.length > 0 ? 'lg:grid-cols-2' : ''} gap-5`}>
          {/* Monthly Trend Chart */}
          {dashboardData.monthlyTrend.length > 0 && (
            <BirthsByMonthChart 
              data={dashboardData.monthlyTrend}
              selectedYear={selectedYear}
              clinicName={selectedClinicName}
            />
          )}

          {/* Clinic Comparison Chart */}
          <BirthsByClinicChart 
            data={dashboardData.clinicStats}
            selectedYear={selectedYear}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
